import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AnimationOptions } from 'ngx-lottie';
import { AppSettings } from 'src/app/app-settings';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { ReportsService } from 'src/app/services/reports.service';
import {
  eventDateLong,
  eventDateShort,
  purchaseDate
} from 'src/app/utils/profile-dates.util';
import { CalendarEvent, calendarPlatform, iosSinSafari, icsUrl, openGoogleCalendar, outlookCalendarUrl } from 'src/app/utils/calendar-links.util';

/**
 * La pantalla de una entrada.
 *
 * No es compartible a propósito: la URL lleva el id del pedido y la API lo
 * acota al usuario que ha iniciado sesión, así que un enlace pasado a otra
 * persona no le abre nada. Por eso tampoco hay botón de compartir.
 *
 * Un pedido puede mezclar varios eventos, así que la pantalla enseña solo el
 * grupo que venía marcado desde el listado: se navega entre las entradas de
 * ese evento, no entre las de todo el pedido.
 */
@Component({
  selector: 'app-ticket-detail',
  templateUrl: './ticket-detail.component.html',
  styleUrls: ['./ticket-detail.component.scss']
})
export class TicketDetailComponent implements OnInit, OnDestroy {

  /**
   * Alto de la franja rosa del pase, para que las entradas que asoman por
   * detrás lleven la suya y acabe a la misma altura (ver `.pass-stack` en el
   * scss). No es fijo: cambia si el título ocupa una línea o dos y si hay
   * flechas, así que se mide en vivo y se deja en `--pass-head-h`.
   */
  private observadorCabecera?: ResizeObserver;

  @ViewChild('passHead') set passHead(ref: ElementRef<HTMLElement> | undefined) {
    this.observadorCabecera?.disconnect();

    if (!ref || typeof ResizeObserver === 'undefined') {
      return;
    }

    const cabecera = ref.nativeElement;
    this.observadorCabecera = new ResizeObserver(() => {
      const mazo = cabecera.closest('.pass-stack') as HTMLElement | null;
      mazo?.style.setProperty('--pass-head-h', `${cabecera.offsetHeight}px`);
    });
    this.observadorCabecera.observe(cabecera);
  }

  ngOnDestroy(): void {
    this.observadorCabecera?.disconnect();
  }

  loading: boolean = true;
  notFound: boolean = false;

  order: any = null;
  /** Las entradas del evento que se está mirando. */
  tickets: any[] = [];
  current: number = 0;

  urlEvents: string = AppSettings.MEDIA_URI + '/events_images/';

  eventTitle: string = '';
  eventImage: string | null = null;
  spaceName: string | null = null;
  address: string | null = null;
  dateLong: string = '';
  dateShort: string = '';
  startDate: string | null = null;
  eventId: number | null = null;
  sessionId: number | null = null;

  orderNumber: string = '';
  purchasedOn: string = '';
  total: number = 0;
  isSeasonPassOrder: boolean = false;

  /** Cuántas entradas tenía este evento antes de quitar las devueltas. */
  totalTickets: number = 0;
  refundedCount: number = 0;

  /** Menú de "Añadir al calendario". */
  showCalendar: boolean = false;

  /**
   * Wallet. Mismo comportamiento que el carné del abono: una sola insignia, la
   * del wallet que le sirve al dispositivo, y el spinner debajo.
   */
  walletEnabled: boolean = AppSettings.WALLET;
  walletLoading: 'google' | 'apple' | null = null;
  /**
   * Qué se está añadiendo: la entrada que se ve (la insignia del pase) o
   * todas las del evento (la tarjeta "Wallet"). Cada una enseña su spinner y
   * su error en su sitio.
   */
  walletAlcance: 'una' | 'todas' | null = null;
  walletError: { texto: string; alcance: 'una' | 'todas' } | null = null;

  /** El evento y la sesión del grupo que se ve, para pedir todas sus entradas. */
  private idEvent: number | null = null;
  private idSession: number | null = null;

  /** Aviso de "Localizador copiado", que se apaga solo a los 2,5s. */
  copiado: boolean = false;
  private avisoCopiado: any;

  loadingLottie: AnimationOptions = {
    path: 'assets/lotties/loading.json',
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private reportService: ReportsService
  ) { }

  ngOnInit(): void {
    const idOrder = Number(this.route.snapshot.paramMap.get('order'));
    const idEvent = Number(this.route.snapshot.queryParamMap.get('event')) || null;
    const idSession = Number(this.route.snapshot.queryParamMap.get('session')) || null;

    if (!idOrder) {
      this.notFound = true;
      this.loading = false;
      return;
    }

    this.reportService.getOrderByUser(idOrder).subscribe({
      next: (res) => {
        const order = res?.data;
        if (!order) {
          this.notFound = true;
          this.loading = false;
          return;
        }
        this.build(order, idEvent, idSession);
        this.loading = false;
      },
      error: () => {
        this.notFound = true;
        this.loading = false;
      }
    });
  }

  /**
   * Se queda con las líneas del evento marcado. Si no viene marca —o apunta a
   * un evento que no está en este pedido, que es lo que pasa al tocar la URL a
   * mano—, se coge el primer evento del pedido, que en la inmensa mayoría de
   * pedidos es el único.
   *
   * La sesión hace falta además del evento porque un mismo evento puede tener
   * varias funciones en el mismo pedido; cuando no la hay se ignora.
   */
  private build(order: any, idEvent: number | null, idSession: number | null) {
    this.order = order;

    const cartLines = order.cart?.cart_lines || [];

    this.isSeasonPassOrder = cartLines.length > 0 && cartLines.every((line: any) =>
      (line.bookings || []).length > 0 && line.bookings.every((booking: any) => !!booking.id_season_pass));

    const esDeLaSesion = (line: any) => !idSession || (line.session?.id ?? null) === idSession;
    const pedidas = cartLines.filter((line: any) => line.event?.id === idEvent && esDeLaSesion(line));

    // Sin evento válido, el del primer grupo del pedido.
    const referencia = pedidas.length ? pedidas[0] : cartLines[0];
    const lineas = pedidas.length
      ? pedidas
      : cartLines.filter((line: any) =>
          line.event?.id === referencia?.event?.id &&
          (line.session?.id ?? null) === (referencia?.session?.id ?? null));

    const primera = lineas[0];

    if (!primera) {
      this.notFound = true;
      return;
    }

    const event = primera.event || {};
    this.idEvent = event.id ?? null;
    this.idSession = primera.session?.id ?? null;
    this.startDate = primera.session?.start_date || event.start_date || null;
    this.eventId = event.id ?? null;
    this.sessionId = primera.session?.id ?? null;
    this.eventTitle = event.title || '';
    this.eventImage = event.image_url || null;
    this.spaceName = event.space?.name || null;
    this.address = event.space?.address || event.address || null;
    this.dateLong = eventDateLong(this.startDate);
    this.dateShort = eventDateShort(this.startDate);

    this.orderNumber = order.invoice_number;
    this.purchasedOn = purchaseDate(order.created_at);

    lineas.forEach((line: any) => {
      (line.bookings || []).forEach((booking: any) => {
        this.tickets.push(this.buildTicket(order, line, booking));
        this.total += Number(booking.price ?? line.price ?? 0);
      });
    });

    // Una entrada devuelta no se enseña: ni QR, ni descarga, ni wallet. Si lo
    // están todas, aquí no hay nada que ver.
    //
    // Pero hay que decirlo: el listado anuncia "2 entradas" y aquí salía
    // "Entrada 1 de 1" sin explicar dónde había ido la otra.
    this.totalTickets = this.tickets.length;
    this.lineasRecibo = this.tickets.map(ticket => this.lineaRecibo(ticket));
    this.refundedCount = this.tickets.filter(ticket => ticket.refund).length;
    this.tickets = this.tickets.filter(ticket => !ticket.refund);

    if (this.tickets.length === 0) {
      this.notFound = true;
    }
  }

  private buildTicket(order: any, line: any, booking: any) {
    const seat = booking?.seat;
    // Una reserva de abonado no tiene ticket ni hash propio: el nombre sale
    // del abono y el QR de la puerta es el del propio abono.
    const isSeasonPass = !!booking?.id_season_pass;
    const seasonPass = booking?.season_pass || null;

    return {
      id: booking.id,
      ticketName: isSeasonPass ? 'Abono de temporada' : (line.ticket?.name || ''),
      isSeasonPass,
      seasonPassNumber: seasonPass?.member_number || null,
      price: booking.price ?? line.price ?? 0,
      sectionName: seat?.map_section?.name || null,
      seat: seat?.seat || null,
      row: seat?.row || null,
      // Qué parte de la posición enseña esta sección. `seat.hidden` es otra
      // cosa y está siempre a false: manda la configuración de la sección.
      showRow: (seat?.map_section?.show_row ?? true) !== false,
      showSeatLabel: (seat?.map_section?.show_seat_label ?? true) !== false,
      localizador: booking?.hash || seasonPass?.hash || '',
      refund: booking.status?.name == 'refunded',
      // El titular se congela en el pedido, no en cada entrada.
      nominative: !!line.event?.nominative_tickets && !!order.holder_name,
      holderName: order.holder_name || null
    };
  }

  /**
   * "Platea · Fila 3 · Butaca 7", con lo que enseñe la sección (algunas no
   * llevan fila, o no dicen "Butaca"); sin butaca, el tipo de entrada. El tipo
   * va además debajo cuando hay butaca, que es lo que distingue una de adulto
   * de una infantil en la misma fila.
   */
  private lineaRecibo(ticket: any) {
    const partes: string[] = [];
    if (ticket.sectionName) {
      partes.push(ticket.sectionName);
    }
    if (ticket.row && ticket.showRow) {
      partes.push('Fila ' + ticket.row);
    }
    if (ticket.seat) {
      partes.push(ticket.showSeatLabel ? 'Butaca ' + ticket.seat : String(ticket.seat));
    }

    const conButaca = partes.length > 0;
    return {
      texto: conButaca ? partes.join(' · ') : (ticket.ticketName || 'Entrada'),
      tipo: conButaca && ticket.ticketName ? ticket.ticketName : null,
      precio: Number(ticket.price) || 0,
      devuelta: !!ticket.refund
    };
  }

  get lineasReciboVisibles() {
    return this.reciboAbierto ? this.lineasRecibo : this.lineasRecibo.slice(0, this.reciboPlegadoHasta);
  }

  get ticket() {
    return this.tickets[this.current] || null;
  }

  /**
   * El aviso de las devueltas. Lo único que hay que explicar es por qué aquí
   * se ven menos entradas de las que anunciaba el listado, así que se dice eso
   * y se acaba.
   */
  get refundNotice(): string {
    const cuantas = this.refundedCount === 1
      ? `1 de las ${this.totalTickets} entradas está devuelta`
      : `${this.refundedCount} de las ${this.totalTickets} entradas están devueltas`;

    return `${cuantas}. Aquí solo aparecen las que puedes usar.`;
  }

  prev() {
    this.cambiarA(this.current - 1);
  }

  next() {
    this.cambiarA(this.current + 1);
  }

  /** El pase de delante, para animar el cambio de entrada. */
  @ViewChild('passEl') private passEl?: ElementRef<HTMLElement>;

  /**
   * Cambio de entrada como en una baraja: la de arriba se va al FONDO del
   * montón y la de debajo queda arriba (ver `.fantasma-*` y `.fase-*` en el
   * scss).
   *
   * Con un solo elemento no se puede: mientras la de arriba viaja al fondo, la
   * que queda debajo tendría que enseñar ya la entrada siguiente, y la tarjeta
   * es una. Así que se hace una COPIA de la que se va (`fantasma`), que es la
   * que hace el viaje, y el pase de verdad cambia de entrada al momento y sube
   * a su sitio por debajo. Así, al apartarse la de arriba se ve la entrada
   * siguiente y no una capa en blanco.
   */
  private cambiarA(indice: number) {
    if (indice === this.current || indice < 0 || indice >= this.tickets.length) {
      return;
    }

    this.olvidarWallet();
    const haciaDelante = indice > this.current;
    const pase = this.passEl?.nativeElement;

    // Con "reducir movimiento" no hay animación: cambio directo.
    if (!pase || this.sinMovimiento()) {
      this.current = indice;
      return;
    }

    // La copia de la que se va: al fondo del montón (siguiente) o un puesto
    // hacia abajo (anterior).
    this.fantasma(pase, haciaDelante ? 'fantasma-al-fondo' : 'fantasma-baja');
    this.current = indice;

    // El pase de verdad NO se mueve: lleva las flechas, y si viajara no se
    // podría volver a pulsar hasta que llegase. Hacia delante solo se asienta
    // en su sitio; hacia atrás se queda transparente (pero respondiendo a los
    // clics) mientras una copia de la entrada anterior hace el viaje desde el
    // fondo, y aparece cuando la copia aterriza.
    //
    // La clase se quita, se fuerza un reflow y se vuelve a poner: una animación
    // CSS no se repite si la clase ya estaba, y con dos clics seguidos lo
    // estaría.
    pase.classList.remove('fase-entra-next', 'fase-entra-prev');
    void pase.offsetWidth;

    // Si viene de arrastrar con el dedo, la entrada nueva YA está a la vista
    // en su sitio y a su tamaño (era la que asomaba debajo, `previewIndice`):
    // no se anima nada, solo la que se suelta se va al montón. Hacerla venir
    // del fondo, como con la flecha, la quitaba de ahí y durante el viaje se
    // veía la capa del abanico en blanco; y el "asentarse" de la flecha
    // (crecer desde un 98,5%) se veía como un latido sobre algo que ya estaba
    // quieto.
    if (this.desdeArrastre) {
      return;
    }

    pase.classList.add(haciaDelante ? 'fase-entra-next' : 'fase-entra-prev');

    if (!haciaDelante) {
      // La copia de la entrada anterior tiene que salir del pase YA con su
      // contenido, que Angular pinta al acabar este ciclo: por eso en el
      // siguiente.
      setTimeout(() => this.fantasma(pase, 'fantasma-del-fondo'));
    }
  }

  /** Al acabar su animación, el pase se queda sin clase de fase. */
  finDeFase(evento: AnimationEvent) {
    if (evento.target === this.passEl?.nativeElement) {
      this.passEl!.nativeElement.classList.remove('fase-entra-next', 'fase-entra-prev');
    }
  }

  /**
   * Una copia del pase, encima de él y con su animación, que se borra sola al
   * acabar. Es la que hace los viajes largos, para que el pase de verdad no
   * tenga que moverse.
   *
   * `cloneNode` no copia lo pintado en un canvas, y el QR es un canvas: se
   * vuelve a pintar a mano. Y se repinta un par de veces más: la copia de la
   * entrada anterior se hace justo después de cambiar de entrada, y el QR
   * nuevo puede tardar unos milisegundos en dibujarse.
   */
  private fantasma(pase: HTMLElement, clase: string) {
    const copia = pase.cloneNode(true) as HTMLElement;

    const copiarQr = () => {
      const lienzos = pase.querySelectorAll('canvas');
      copia.querySelectorAll('canvas').forEach((lienzo, i) => {
        const ctx = lienzo.getContext('2d');
        if (ctx && lienzos[i]) {
          ctx.clearRect(0, 0, lienzo.width, lienzo.height);
          ctx.drawImage(lienzos[i], 0, 0);
        }
      });
    };

    copia.classList.remove('fase-entra-next', 'fase-entra-prev');
    copia.classList.add('pass-fantasma', clase);

    // Si viene de un deslizamiento, la copia arranca donde se soltó el dedo
    // y no desde el sitio del pase: si no, daría un salto hacia atrás.
    if (this.desdeArrastre && clase !== 'fantasma-del-fondo') {
      copia.style.setProperty('--desde-x', `${this.desdeArrastre.x}px`);
      copia.style.setProperty('--desde-r', `${this.desdeArrastre.r}deg`);
    }
    copia.setAttribute('aria-hidden', 'true');
    copia.style.width = `${pase.offsetWidth}px`;
    copia.style.height = `${pase.offsetHeight}px`;
    copia.addEventListener('animationend', () => copia.remove(), { once: true });

    pase.parentElement?.appendChild(copia);
    copiarQr();
    if (clase === 'fantasma-del-fondo') {
      setTimeout(copiarQr, 60);
      setTimeout(copiarQr, 150);
    }
  }

  // ===== DESLIZAR CON EL DEDO =====

  /**
   * El arrastre en curso. `horizontal` se decide en los primeros píxeles: si
   * el dedo va más en vertical que en horizontal es que quiere hacer scroll, y
   * se suelta el gesto para que la página se desplace como siempre (el pase
   * lleva `touch-action: pan-y`, ver el scss).
   */
  private arrastre: { id: number; x0: number; y0: number; dx: number; horizontal: boolean | null } | null = null;

  /** Dónde se soltó el dedo, para que la animación arranque desde ahí. */
  private desdeArrastre: { x: number; r: number } | null = null;

  /**
   * La entrada que se pinta DEBAJO mientras se arrastra (ver `.pass-preview`):
   * la siguiente si el dedo va a la izquierda, la anterior si va a la derecha.
   * Sin ella, al apartar la de arriba solo se veían las capas del abanico, que
   * son tarjetas en blanco.
   */
  previewIndice: number | null = null;

  /** Cuánto hay que arrastrar para pasar de entrada. */
  private readonly umbralArrastre = 60;

  empiezaArrastre(evento: PointerEvent) {
    if (this.tickets.length < 2 || evento.button > 0) {
      return;
    }

    // Con el dedo vale en cualquier parte del pase. Con el ratón también se
    // puede (así funciona en escritorio y en el modo responsive del navegador,
    // que según cómo esté configurado manda el arrastre como ratón y no como
    // dedo), pero no si empieza sobre un botón, el campo del número o el
    // localizador, que se tiene que poder seleccionar para copiarlo.
    if (evento.pointerType === 'mouse'
      && (evento.target as HTMLElement).closest('button, input, a, .pass-locator')) {
      return;
    }

    this.arrastre = { id: evento.pointerId, x0: evento.clientX, y0: evento.clientY, dx: 0, horizontal: null };
  }

  arrastra(evento: PointerEvent) {
    const a = this.arrastre;
    const pase = this.passEl?.nativeElement;
    if (!a || !pase || evento.pointerId !== a.id) {
      return;
    }

    const dx = evento.clientX - a.x0;
    const dy = evento.clientY - a.y0;

    if (a.horizontal === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
        return;
      }
      a.horizontal = Math.abs(dx) > Math.abs(dy);
      if (!a.horizontal) {
        this.arrastre = null;
        return;
      }
      pase.setPointerCapture?.(evento.pointerId);
      // Que arrastrar no vaya seleccionando texto por el camino.
      pase.classList.add('arrastrando');
      window.getSelection()?.removeAllRanges();
    }

    // En la primera y en la última entrada el pase se resiste: se mueve a un
    // cuarto, para que se note que por ahí no hay más.
    const sinMas = (dx < 0 && this.current >= this.tickets.length - 1) || (dx > 0 && this.current <= 0);
    a.dx = sinMas ? dx / 4 : dx;

    pase.style.transition = 'none';
    pase.style.transform = `translateX(${a.dx}px) rotate(${a.dx / 40}deg)`;

    const lado = a.dx < 0 ? this.current + 1 : this.current - 1;
    const debajo = lado >= 0 && lado < this.tickets.length ? lado : null;
    if (this.previewIndice !== debajo) {
      this.previewIndice = debajo;
    }
  }

  sueltaArrastre(evento: PointerEvent, cancelado: boolean = false) {
    const a = this.arrastre;
    const pase = this.passEl?.nativeElement;
    if (!a || !pase || evento.pointerId !== a.id) {
      return;
    }
    this.arrastre = null;
    pase.classList.remove('arrastrando');

    if (!a.horizontal) {
      return;
    }

    const destino = cancelado ? null
      : a.dx <= -this.umbralArrastre ? this.current + 1
      : a.dx >= this.umbralArrastre ? this.current - 1
      : null;

    if (destino !== null && destino >= 0 && destino < this.tickets.length) {
      // El pase vuelve a su sitio de golpe: lo tapa la copia, que arranca
      // justo donde estaba él (`desdeArrastre`).
      pase.style.transition = '';
      pase.style.transform = '';
      this.desdeArrastre = { x: a.dx, r: a.dx / 40 };
      this.cambiarA(destino);
      this.desdeArrastre = null;
      // El pase de verdad ya enseña la entrada que estaba debajo, en el mismo
      // sitio: la de debajo sobra y se quita sin que se note.
      this.previewIndice = null;
      return;
    }

    // No llegó al umbral (o no había más entradas): vuelve a su sitio.
    pase.style.transition = 'transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)';
    pase.style.transform = '';
    setTimeout(() => {
      if (!this.arrastre) {
        pase.style.transition = '';
        // Ya ha vuelto a su sitio y tapa la de debajo: se puede quitar.
        this.previewIndice = null;
      }
    }, 240);
  }

  private sinMovimiento(): boolean {
    return typeof window !== 'undefined'
      && !!window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Al cambiar de entrada el pase es otro, así que el error de la anterior no
   * pinta nada y la carga a medias tampoco: lo que llegue se descarta porque
   * ya no se está esperando (ver `terminaWallet`). Lo de "todas" no depende
   * de la entrada que se vea: se deja seguir.
   */
  private olvidarWallet() {
    if (this.walletAlcance === 'una') {
      this.walletLoading = null;
      this.walletAlcance = null;
    }
    if (this.walletError?.alcance === 'una') {
      this.walletError = null;
    }
  }

  /**
   * A partir de aquí las flechas no bastan: hay pedidos de más de cien
   * entradas y nadie va a pulsar cien veces. Por debajo se deja el texto de
   * siempre, que es la inmensa mayoría de los pedidos y un campo para teclear
   * ahí solo estorba.
   */
  get muchasEntradas(): boolean {
    return this.tickets.length > 10;
  }

  /**
   * Salta a la entrada que se teclee. Se recorta al rango en vez de ignorar
   * el valor: quien escribe 500 en un pedido de 136 quiere la última.
   */
  irAEntrada(valor: any) {
    const numero = parseInt(valor, 10);

    if (isNaN(numero)) {
      return;
    }

    this.cambiarA(Math.min(Math.max(numero, 1), this.tickets.length) - 1);
  }

  back() {
    this.router.navigate(['/profile/entradas']);
  }

  /**
   * Copia el localizador al portapapeles.
   *
   * `navigator.clipboard` solo existe en HTTPS (o en localhost), así que hay
   * una vía de escape con un textarea invisible: en un móvil viejo o en una
   * pre servida por http el botón seguiría funcionando.
   */
  copiarLocalizador(tooltip?: MatTooltip) {
    const texto = this.ticket?.localizador;
    if (!texto) {
      return;
    }

    // El aviso va en el propio tooltip. Se fuerza a mostrarse porque en móvil
    // no hay hover que lo abra, y se vuelve a pintar para que recoja el texto
    // nuevo si ya estaba abierto.
    const avisar = () => {
      this.copiado = true;
      tooltip?.show();

      clearTimeout(this.avisoCopiado);
      this.avisoCopiado = setTimeout(() => {
        this.copiado = false;
        tooltip?.hide();
      }, 2500);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(texto).then(avisar, () => this.copiarALaAntigua(texto) && avisar());
      return;
    }

    if (this.copiarALaAntigua(texto)) {
      avisar();
    }
  }

  private copiarALaAntigua(texto: string): boolean {
    const campo = document.createElement('textarea');
    campo.value = texto;
    // Fuera de la pantalla y sin poder recibir el foco visible, para que no
    // dé un salto de scroll al seleccionarlo.
    campo.style.position = 'fixed';
    campo.style.opacity = '0';
    document.body.appendChild(campo);
    campo.select();

    let copiado = false;
    try {
      copiado = document.execCommand('copy');
    } catch {
      copiado = false;
    }

    document.body.removeChild(campo);
    return copiado;
  }

  toggleCalendar() {
    this.showCalendar = !this.showCalendar;
  }

  /** Un clic fuera lo cierra, que si no se queda abierto para siempre. */
  @HostListener('document:click', ['$event'])
  cerrarCalendario(event: any) {
    if (this.showCalendar && !event.target.closest('.calendar-menu')) {
      this.showCalendar = false;
    }
  }

  private get eventoDeCalendario(): CalendarEvent {
    return {
      title: this.eventTitle,
      startDate: this.startDate,
      location: [this.spaceName, this.address].filter(Boolean).join(', ') || null,
      eventId: this.eventId,
      sessionId: this.sessionId
    };
  }

  /**
   * La opción del .ics cambia de nombre y de sitio según el dispositivo. En
   * iPhone va la primera: abre la hoja nativa, que además guarda en la cuenta
   * de Google o de Outlook si el teléfono la tiene, cosa que las webs no.
   */
  readonly plataforma = calendarPlatform();
  /** iPhone sin Safari: el .ics no llega al Calendario (ver `iosSinSafari`). */
  readonly sinSafari = iosSinSafari();

  get nativeCalendarLabel(): string {
    if (this.plataforma === 'ios') {
      return 'Calendario del iPhone';
    }
    return this.plataforma === 'mac' ? 'Apple Calendar' : 'Otro calendario (.ics)';
  }

  /**
   * El .ics es un enlace y el navegador tiene que seguirlo. Si el menú se
   * cierra en el propio clic, Angular quita el enlace de la página antes de
   * que el navegador navegue y el clic se pierde: "Calendario del iPhone" no
   * hacía nada. Se cierra justo después.
   */
  cerrarCalendarioTrasNavegar() {
    setTimeout(() => this.showCalendar = false);
  }

  get icsHref(): string | null {
    return icsUrl(this.eventoDeCalendario);
  }

  /** Google Calendar con el evento ya relleno; en Android, en la app si se puede. */
  addToGoogleCalendar() {
    openGoogleCalendar(this.eventoDeCalendario);
    this.showCalendar = false;
  }

  /** Abre Outlook con el evento ya relleno. */
  addToOutlookCalendar() {
    const url = outlookCalendarUrl(this.eventoDeCalendario);
    if (url) {
      window.open(url, '_blank');
    }
    this.showCalendar = false;
  }

  // ===== WALLET =====

  /**
   * Se ofrece una sola insignia, la del wallet que le sirve al dispositivo: la
   * de Apple solo en iPhone y iPad, y la de Google en todo lo demás.
   *
   * El Mac queda fuera a propósito, aunque Safari sepa guardar el `.pkpass` en
   * el Wallet de iCloud: en un escritorio no se sabe qué teléfono lleva quien
   * mira, y el enlace de Google vale para cualquiera. Y las normas de marca de
   * Apple piden no ofrecer el pase donde no se puede guardar.
   *
   * El iPad hay que reconocerlo por el táctil: desde iPadOS 13 Safari se
   * anuncia como `Macintosh` y por el user agent no se distingue de un Mac.
   */
  get appleWalletAvailable(): boolean {
    const userAgent = navigator.userAgent || '';
    const isIpadOs = /Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;

    return /iPad|iPhone|iPod/.test(userAgent) || isIpadOs;
  }

  /**
   * Las entradas de este evento que pueden ir al Wallet. Las de abonado no:
   * su pase es el del abono, que ya se añade desde su carné.
   */
  get walletTickets(): any[] {
    return this.tickets.filter(ticket => !ticket.isSeasonPass && ticket.localizador);
  }

  get walletTicketsCount(): number {
    return this.walletTickets.length;
  }

  /**
   * La insignia del pase añade SOLO la entrada que se ve; la de la tarjeta
   * "Wallet", todas las del evento (un pase por entrada, cada uno con su QR,
   * y Wallet los apila). Las dos son la insignia oficial: las normas de marca
   * de Google y Apple no dejan lanzar la acción con un enlace o un botón
   * propio. Lo que las distingue es el texto de alrededor, que la insignia
   * no se puede rotular.
   */
  get walletAvailable(): boolean {
    return this.walletEnabled && !!this.ticket && !this.ticket.isSeasonPass && !!this.ticket.localizador;
  }

  /** La tarjeta de "todas": solo con más de una, que con una es lo mismo. */
  get walletVarias(): boolean {
    return this.walletEnabled && this.walletTicketsCount > 1;
  }

  /** Pide el enlace de Google Wallet y lo abre. Los pases se firman en el servidor. */
  addToGoogleWallet(todas: boolean = false) {
    if (this.walletLoading || (todas ? !this.walletVarias : !this.walletAvailable)) {
      return;
    }

    this.empiezaWallet('google', todas);

    const peticion = todas
      ? this.reportService.getGoogleWalletUrlForEvent(this.order.id, this.idEvent!, this.idSession)
      : this.reportService.getGoogleWalletUrl(this.ticket.id);

    peticion.subscribe({
      next: (res) => this.terminaWallet(todas, res?.data?.save_url, url => window.open(url, '_blank')),
      error: (err) => this.terminaWallet(todas, null, () => {}, err?.error?.message)
    });
  }

  /**
   * Pide la URL firmada del `.pkpass` (o del `.pkpasses` con todas) y
   * **navega** a ella, no la abre en otra pestaña: iOS solo ofrece "Añadir a
   * Wallet" cuando el fichero llega por una navegación normal. Caduca a los 5
   * minutos, así que se pide en cada clic.
   */
  addToAppleWallet(todas: boolean = false) {
    if (this.walletLoading || (todas ? !this.walletVarias : !this.walletAvailable)) {
      return;
    }

    this.empiezaWallet('apple', todas);

    const peticion = todas
      ? this.reportService.getAppleWalletUrlForEvent(this.order.id, this.idEvent!, this.idSession)
      : this.reportService.getAppleWalletUrl(this.ticket.id);

    peticion.subscribe({
      next: (res) => this.terminaWallet(todas, res?.data?.download_url, url => window.location.href = url),
      error: (err) => this.terminaWallet(todas, null, () => {}, err?.error?.message)
    });
  }

  private empiezaWallet(plataforma: 'google' | 'apple', todas: boolean) {
    this.walletLoading = plataforma;
    this.walletAlcance = todas ? 'todas' : 'una';
    this.walletError = null;
  }

  private terminaWallet(todas: boolean, url: string | null | undefined, abrir: (url: string) => void, mensaje?: string) {
    const alcance = todas ? 'todas' : 'una';
    if (this.walletAlcance !== alcance) {
      return;
    }

    this.walletLoading = null;
    this.walletAlcance = null;

    if (url) {
      abrir(url);
      return;
    }

    this.walletError = {
      texto: mensaje || 'No se ha podido generar el pase. Inténtalo de nuevo más tarde.',
      alcance
    };
  }

  openMaps() {
    const destino = [this.spaceName, this.address].filter(Boolean).join(', ');
    if (!destino) {
      return;
    }
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destino)}`, '_blank');
  }

  /**
   * El PDF sale del TPV y es por PEDIDO, no por entrada: no hay forma de
   * pedirle solo una ni solo las de un evento. Por eso el botón dice
   * "Descargar entradas del pedido": si el pedido trae varios eventos, bajan
   * todos.
   */
  /**
   * En móvil la pantalla va en dos pestañas: lo que se enseña en la puerta
   * (las entradas, la descarga, Wallet) y el resto (cartel, fecha, lugar,
   * pedido). En escritorio no hay pestañas: se ve todo a la vez.
   */
  pestanaMovil: 'entradas' | 'evento' = 'entradas';

  /**
   * El recibo de "Evento y pedido" en móvil: una línea por entrada, las
   * devueltas incluidas (tachadas), porque el total del pedido también las
   * cuenta. Se calcula antes de quitar las devueltas de `tickets`.
   */
  lineasRecibo: { texto: string; tipo: string | null; precio: number; devuelta: boolean }[] = [];
  /** Con muchas entradas el recibo se pliega y se abre a petición. */
  reciboAbierto: boolean = false;
  readonly reciboPlegadoHasta = 2;

  /** La descarga es una petición al TPV y tarda: el botón lo enseña. */
  descargando: boolean = false;

  downloadTickets() {
    if (this.isSeasonPassOrder || this.descargando) {
      return;
    }

    this.descargando = true;

    this.reportService.getUrlsDownload(this.order.id).subscribe({
      next: (response) => {
        this.descargando = false;
        const url = response?.data?.ticket_a4;
        if (url) {
          const newWindow = window.open(url, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            window.location.href = url;
          }
        } else {
          this.openInfoCard('No se pudo obtener la URL de descarga del ticket.', true, false);
        }
      },
      error: () => {
        this.descargando = false;
        this.openInfoCard('Lo sentimos, no se han podido descargar las entradas.', true, false);
      }
    });
  }

  openInfoCard(msg: string, disabledClose: boolean = false, icon: boolean = true) {
    this.dialog.open(InfoCardComponent, {
      panelClass: ['info-dialog'],
      disableClose: disabledClose,
      data: {
        icon: icon,
        text: msg,
        btnCancel: false
      }
    });
  }
}
